import classNames from 'classnames';
import CountAnimatedBadge from 'components/atoms/CountAnimatedBadge/CountAnimatedBadge.component';
import CountProgressBadge from 'components/atoms/CountProgressBadge.component';
import UseEffect from 'components/atoms/UseEffect';
import dayjs from 'dayjs';
import { useSubscriptionPlan } from 'hooks/Subscriptions';
import useFeatureCounts from 'hooks/Subscriptions/useFeatureCounts';
import useUpdateSidebarDocCounts, {
  useSidebarState,
} from 'hooks/useUpdateSidebarDocCounts';
import type { FC } from 'react';
import { SidebarDocCountsKeysEnum } from 'store/sidebarState';
import { SubscriptionPlanNameEnum } from 'types/users.types';
import { useTranslation } from 'react-i18next';

const getNextExpenseLimitResetDate = (start: Date | undefined) => {
  if (!start) return undefined;

  const anchor = dayjs(start).startOf('day');

  if (!anchor.isValid()) return undefined;

  const today = dayjs().startOf('day');

  let monthsAhead = 0;

  while (!anchor.add(monthsAhead, 'month').isAfter(today)) {
    monthsAhead += 1;
  }

  return anchor.add(monthsAhead, 'month');
};

const NewExpensesCount: FC<{ isActive?: boolean }> = ({ isActive }) => {
  const { t } = useTranslation();
  const { state: sidebarState } = useSidebarState();
  const newlyAddedExpensesCount =
    sidebarState?.[SidebarDocCountsKeysEnum.NewExpensesCount];

  const updateSidebarCounts = useUpdateSidebarDocCounts();

  const featureCounts = useFeatureCounts();

  const subscriptionPlan = useSubscriptionPlan();

  const nextBillingReset = getNextExpenseLimitResetDate(
    subscriptionPlan?.start,
  );

  const limitResetTooltip = nextBillingReset
    ? t('sidebar.expenses_monthly_limit.tooltip_text', {
        nextResetDate: nextBillingReset.format('MMMM D, YYYY'),
      })
    : t('sidebar.expenses_monthly_limit.tooltip_text.fallback');

  const currentExpensesCount = featureCounts.data?.monthlyExpenses || 0;

  const planAllowedExpensesCount = (subscriptionPlan?.features?.counters as any)
    ?.monthlyExpenses?.total;

  const remainingExpenses = Math.max(
    0,
    planAllowedExpensesCount - currentExpensesCount,
  );

  const isInvoicingPlusUser = [
    SubscriptionPlanNameEnum.e_invoicing1,
    SubscriptionPlanNameEnum.e_invoicing12,
  ].includes(subscriptionPlan?.planInternalName as SubscriptionPlanNameEnum);

  const shouldDisplayRemainingExpenses =
    featureCounts.data != null &&
    isInvoicingPlusUser &&
    Number.isFinite(planAllowedExpensesCount) &&
    planAllowedExpensesCount > 0 &&
    Number.isFinite(currentExpensesCount);

  if (shouldDisplayRemainingExpenses) {
    return (
      <CountProgressBadge
        tooltipContent={limitResetTooltip}
        percentage={(currentExpensesCount / planAllowedExpensesCount) * 100}
        count={remainingExpenses}
        color="expenses"
        className="ml-2"
      />
    );
  }

  if (!newlyAddedExpensesCount) return null;

  return (
    <UseEffect
      effect={() => {
        const id = setTimeout(
          () =>
            updateSidebarCounts(SidebarDocCountsKeysEnum.NewExpensesCount, 0),
          3000,
        );

        return () => clearTimeout(id);
      }}
      key={String(newlyAddedExpensesCount)}
      deps={[newlyAddedExpensesCount]}
      render={() => (
        <CountAnimatedBadge
          count={`+${newlyAddedExpensesCount}`}
          className={classNames(
            '!text-expenses-900 border-[1px] border-expenses-900',
            {
              '!bg-white': isActive,
              '!bg-expenses-100': !isActive,
            },
          )}
        />
      )}
    />
  );
};

export default NewExpensesCount;
